import React, { useState } from 'react';
import cn from 'classnames';

import { beVerifyStripeIdentityWrite } from 'actions/salon/beVerifyStripeIdentity.action';
import SalonModal from 'components/dialog/SalonModal.component';
import Button from 'components/form/Button.component';
import AlertPanel from 'components/generic/AlertPanel.component';
import ExternalLink from 'components/generic/ExternalLink.component';
import Icon from 'components/generic/Icon.component';
import IconStatus from 'components/generic/IconStatus.component';
import Space from 'components/generic/Space.component';
import useCallbackUnignored from 'hooks/useCallbackUnignored.hook';
import { usePrepareWriteOnly } from 'hooks/usePrepare.hook';
import { useTranslation } from 'providers/GILT.provider';
import { useIsSalonRestrictedToCriticalServices } from 'providers/SalonBasicInfo.provider';
import fromAnyToBool from 'utils/converters/fromAnyToBool.util';
import { locationNew } from 'utils/dom/location.util';
import logger from 'utils/logger.util';
import isFn from 'utils/predicates/function/isFn.util';
import { isValidUrl } from 'utils/predicates/isValid.util';
import { concatTag as ct } from 'utils/strings/manipulate.util';

import CN from './StripeAccountVerifierModal.module.scss';

const CTAG = 'StripeAccountVerifierModal';
const L = logger(CTAG);

const StripeAccountVerifierModal = ({ enabled, tag, onClose, onCancel }) => {
  const { t } = useTranslation();
  const [generatedUrl, setGeneratedUrl] = useState('');
  const { up: generateVerificationLink } = usePrepareWriteOnly(beVerifyStripeIdentityWrite);

  const [errorMessage, setErrorMessage] = useState('');

  const handleStart = useCallbackUnignored(
    async () => {
      const FTAG = 'handleStart()';

      setErrorMessage('');
      const result = await generateVerificationLink();
      const url = result?.json?.verificationLink;

      L.debug(FTAG, 'result:', result);

      if (url) {
        setGeneratedUrl(url);
        locationNew({ url });
        return;
      }

      if (!isValidUrl(url)) {
        L.warn(FTAG, 'Invalid verification url:', url);
        setErrorMessage('Received bad verification URL from server');
      }

      if (result?.ok) {
        L.warn(FTAG, 'Verification link received.');
      } else {
        L.warn(FTAG, 'Verification setup failed.', 'result:', result);
      }
    },
    [generateVerificationLink],
    CTAG,
    'handleAcceptContinue()',
  );

  const hasUrl = fromAnyToBool(generatedUrl);

  return (
    <SalonModal tag={ct('VerifyStripeAccountModal', tag)} isOpen={enabled} withFooter>
      <SalonModal.Header>
        <div className={CN.closeButtonContainer}>
          {isFn(onClose) && (
            <Button variant="empty" onClick={onClose}>
              <Icon variant="close" variantInvert />
            </Button>
          )}
        </div>
      </SalonModal.Header>
      <SalonModal.Body>
        <IconStatus variant="alert" width="lg">
          <Icon variant="alert" width="l1" />
        </IconStatus>
        <div className={CN.modalContent}>
          <div className={CN.modalTitle}>{t('composite.stripeAccountVerifierModal.title')}</div>
          <div className={CN.modalText}>
            {t('composite.stripeAccountVerifierModal.descriptionPart1')}
            <Space bold>{t('composite.stripeAccountVerifierModal.descriptionPart2')}</Space>{' '}
            {t('composite.stripeAccountVerifierModal.descriptionPart3')}
            <Space bold>{t('composite.stripeAccountVerifierModal.descriptionPart4')}</Space> {t('common.or')}{' '}
            <Space bold>{t('composite.stripeAccountVerifierModal.descriptionPart5')}</Space>
            {t('composite.stripeAccountVerifierModal.descriptionPart6')}
          </div>
          <div className={CN.alertOuter}>
            <AlertPanel tag="StripeAccountVerifierModal__alert" variant="alert">
              <div>
                <Space bold>{t('composite.stripeAccountVerifierModal.alertMessagePart1')}</Space>
                {t('composite.stripeAccountVerifierModal.alertMessagePart2')}
                <Space bold>{t('composite.stripeAccountVerifierModal.alertMessagePart3')}</Space>{' '}
                {t('composite.stripeAccountVerifierModal.alertMessagePart4')}
                <Space bold>{t('composite.stripeAccountVerifierModal.alertMessagePart5')}</Space>
                {t('composite.stripeAccountVerifierModal.alertMessagePart6')}
                <br />
                {t('composite.stripeAccountVerifierModal.alertMessagePart7')}
                <Space bold> {t('composite.stripeAccountVerifierModal.alertMessagePart8')}</Space>
              </div>
            </AlertPanel>
          </div>

          <p
            className={cn({
              [CN.modalText]: true,
              [CN.error]: true,
            })}
          >
            {errorMessage}
          </p>
        </div>
      </SalonModal.Body>
      <SalonModal.Footer>
        {hasUrl && (
          <Button
            variant="outline"
            xSpace
            onClick={() => {
              onClose?.();
            }}
          >
            <ExternalLink link={generatedUrl} variant="primaryForce">
              {t('composite.stripeAccountVerifierModal.copyLink')}
            </ExternalLink>
          </Button>
        )}
        <Button
          tag="VerifyStripeAccountModal__start"
          onClick={() => {
            hasUrl && onClose?.();
            handleStart();
          }}
          variant="primaryForce"
          casing="dialog"
          xSpace
        >
          {t('composite.stripeAccountVerifierModal.startVerification')}
        </Button>
      </SalonModal.Footer>
    </SalonModal>
  );
};

const StripeAccountVerifierModalHoc = (props) => {
  const isRestrictedToCriticalServices = useIsSalonRestrictedToCriticalServices();

  return isRestrictedToCriticalServices ? <StripeAccountVerifierModal {...props} /> : null;
};

export default StripeAccountVerifierModalHoc;
